import base64 from 'base-64';
import { getVariableForEnv } from 'utils/env';
import { browserWindow } from 'utils/window';

/*
This is set up to help manage our segment tracking in one place. 

It will additionally log all segment calls if SEGMENT_LOGGING
is set to true in your .env.development - this is for easier development.
*/

const debug = (...args) => {
  if (process.env.SEGMENT_LOGGING) {
    console.log(...args);
  }
};

const getSegment = () => {
  if (browserWindow.analytics) {
    return browserWindow.analytics;
  } else {
    console.error('Segment Error: Cannot find window.analytics');
  }
};

// Setup segment environment keys based on the URL for the deployed app
// This sets up previews and netlify environments to point to stage
const getSegmentKey = () => {
  return getVariableForEnv({
    production: process.env.SEGMENT_BROWSER_KEY_PROD,
    stage: process.env.SEGMENT_BROWSER_KEY_STAGE,
    dev: process.env.SEGMENT_BROWSER_KEY_DEV,
  });
};

export const initSegment = () => {
  const segment = getSegment();
  if (segment) {
    segment.load(getSegmentKey());
  }
};

/*
Track events that are fired in the browser with browserTrack(eventName, eventProperties).
This utility checks that window.anaytics exists and managings the debug logging

While under the hood window.analytics.track() is async, this method is not since 
it's usually fired without the need to check a response. 

debugging info can be found at https://app.segment.com/hometap/sources/marketing_site_development/debugger
*/
export const browserTrack = (event, props) => {
  const segment = getSegment();
  if (segment) {
    debug('SEGMENT TRACK |', event, props);
    segment.track(event, props);
  }
};

export const browserTrackPage = (event, props) => {
  const segment = getSegment();
  if (segment) {
    debug('SEGMENT PAGE |', event, props);
    segment.page(event, props);
  }
};

export const browserIdentify = (user, traits) => {
  const segment = getSegment();
  if (segment) {
    debug('SEGMENT IDENTIFY |', user, traits);
    segment.identify(user, traits);
  }
};

export const browserTrackCTAClick = (props) => {
  browserTrack('CTA Clicked', {
    page_url: window.location.href,
    style: 'primary',
    ...props,
  });
};

/*
Use serverTrack to log essential events that shouldn't be blocked by ad blockers. 
This call is async so that an action can be taken after the request has completed.

debugging info can be found at https://app.segment.com/hometap/sources/netlify_local_dev/debugger

See lambda/analytics for the implementation of this ajax call.
*/
export const serverTrack = async (event, props) => {
  return await serverTrackAndIdentify({
    event: event,
    eventProperties: props,
  });
};

export const serverTrackAndIdentify = async (data, options = {}) => {
  debug('SEGMENT TRACK |', data.event, data.eventProperties);
  data.userID && debug('SEGMENT IDENTIFY | ', data.userId, data.userProperties);

  const segment = getSegment();
  return fetch(`/.netlify/functions/analytics/`, {
    method: 'POST',
    headers: {
      Authorization: 'Basic ' + base64.encode('hometap_api:s23kW6)Zm2wDk3>3RiM8'),
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      event: data.event,
      eventProperties: data.eventProperties,
      userId: data.userId,
      userProperties: data.userProperties,
      anonymousId: segment && segment.user && segment.user().anonymousId(),
      sendToSalesforce: options.sendToSalesforce,
    }),
  })
    .then((res) => {
      return res.json();
    })
    .catch((err) => {
      console.log('Segment Lambda Error: ' + err);
    });
};

export const trackStateWaitlist = async (formName, props = {}) => {
  return await serverTrackAndIdentify({
    event: 'Engagement Form Completed',
    eventProperties: {
      form_name: `state_waitlist_${formName}`,
      ...props,
    },
    userId: props.email,
    userProperties: props.email && {
      email: props.email,
    },
  });
};
